import React, { Component } from 'react';
import Helmet from 'react-helmet';

import map from 'lodash/map';
import filter from 'lodash/filter';
import sample from 'lodash/sample';
import sampleSize from 'lodash/sampleSize';
import shuffle from 'lodash/shuffle';

import Image from '../components/Image';
import Video from '../components/Video';
import WorkNav from '../components/WorkNav';
import { ImageCopyBlock, CopyImageBlock } from '../components/ImageCopyBlock';
import Loader from '../components/Loader';
import Animated from '../components/Animated';

import {
  wrapper,
  textContainer,
  fullWidth,
  halfWidth,
  thirdWidth,
  wideContainer,
  removePaddingTop,
} from '../styles';

const getImage = (folder, filename, i) => {
  return [
    {
      srcSet: require(`../assets/${folder}/${filename}-thumb-${i}.jpg`),
      media: '(max-width: 100px)',
    },
    {
      srcSet: require(`../assets/${folder}/${filename}-sm-${i}.jpg`),
      media: '(max-width: 720px)',
    },
    {
      srcSet: require(`../assets/${folder}/${filename}-${i}.jpg`),
    },
  ];
};

const getImages = (folder, filename, count) => {
  let images = [];
  for (let i = 1; i <= count; i++) {
    images.push([
      {
        srcSet: require(`../assets/${folder}/${filename}-sm-${i}.jpg`),
      },
    ]);
  }
  return images;
};

const cloudsImages = getImages(
  'lightblades',
  'lightblades-installation-by-lucastswick-clouds',
  4
);

const warpImages = getImages(
  'lightblades',
  'lightblades-installation-by-lucastswick-warp',
  3
);

const fractalPoster = getImage(
  'lightblades',
  'lightblades-installation-by-lucastswick-fractal',
  Math.floor(Math.random() * 2 + 1)
);

const nightmarePoster = getImage(
  'lightblades',
  'lightblades-installation-by-lucastswick-nightmare',
  Math.floor(Math.random() * 2 + 1)
);

const conceptPoster = getImage(
  'lightblades',
  'lightblades-installation-by-lucastswick-in-context',
  1
);

const lusterPoster = getImage(
  'lightblades',
  'lightblades-installation-by-lucastswick-luster',
  Math.floor(Math.random() * 2 + 1)
);

// const lightbladesMovie = sample(['249484538', '262678548', '262678176']);
const fractalMovie = sample(['262679383', '262679922']);
const lusterMovie = '262700185';
const nightmareMovie = '262700645';

const row1 = (image, index) => {
  return (
    <div {...fullWidth} key={`image-${index}`}>
      <Image sources={image} ratio="16x9" />
    </div>
  );
};

const row2 = (image, index) => {
  return (
    <div {...halfWidth} key={`image-${index}`}>
      <Image sources={image} ratio="16x9" />
    </div>
  );
};

const row3 = (image, index, ratio = '16x9') => {
  return (
    <div {...thirdWidth} key={`image-${index}`}>
      <Image sources={image} ratio={ratio} />
    </div>
  );
};

class Lightblades extends Component {
  constructor(props) {
    super(props);
    this.state = { set1: [], cloudsImages: [], warpImages: [] };
  }

  componentWillMount() {
    this.props.load();

    const fractal = {
      title: 'Fractal Planet',
      excerpt:
        "This shiny geometric concept speaks to the building's technological leadership.",
      headerImage: fractalPoster,
      video: fractalMovie,
    };

    const luster = {
      title: 'Luster',
      excerpt:
        'This concept falls in and out of sync, reminding us that even if things seem chaotic, order eventually returns. ',
      headerImage: lusterPoster,
      video: lusterMovie,
    };

    const nightmare = {
      title: 'Memory',
      excerpt:
        'This piece only plays after 2am when people people should be asleep. The fuzzy shapes overlap to create connections, symbolizing what happens to our memories when we dream.',
      headerImage: nightmarePoster,
      video: nightmareMovie,
    };

    this.setState({
      fractal,
      luster,
      nightmare,
      cloudsImages,
      warpImages,
    });
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.content &&
      nextProps.content.images &&
      !this.state.set1.length
    ) {
      const images = filter(nextProps.content.images, (image, index) => {
        return index !== nextProps.content.headerIndex;
      });
      let set1 = sampleSize(shuffle(images), 4);
      this.setState({ set1 });
    }
  }

  render() {
    const { content } = this.props;
    if (!content) {
      return <Loader />;
    }

    const {
      prev,
      next,
      content: { headerImage },
    } = this.props;
    const {
      set1,
      fractal,
      luster,
      nightmare,
      cloudsImages,
      warpImages,
    } = this.state;

    return (
      <div>
        <Helmet
          title="Lightblades by lucastswick"
          description="The Lightblades are a 3-story tall generative art installation in Portland"
          meta={[
            {
              property: 'og:image:url',
              content: `https://generativeartist.com${headerImage[2].srcSet}`,
            },
            {
              property: 'twitter:image',
              content: `https://generativeartist.com${headerImage[2].srcSet}`,
            },
          ]}
        />
        <div {...textContainer}>
          <Animated as="h1">Lightblades</Animated>
          <Animated delay={150}>
            A 3-story tall generative art installation in Portland
          </Animated>
          <Animated delay={300}>
            In 2017 I worked with the team at{' '}
            <a
              target="_blank"
              rel="nofollow noopener noreferrer"
              href="http://mezr.com/"
            >
              MEZR
            </a>{' '}
            to create a generative art installation that runs on two 3-story
            tall LED lightblades. The lightblades flank Portland's newest
            creative space on Division and 25th, and will continuously create
            new art for years.
          </Animated>
        </div>
        {row1(headerImage, 1)}
        <div {...textContainer}>
          <Animated as="h2">Programming</Animated>
          <Animated>
            The goal for the project was to create a system that continuously
            creates new visuals based on naturally-occuring phenomena. I
            concepted and programmed ten pieces that evolve over time, never
            repeating and continuously mesmerizing.
          </Animated>
        </div>
        <ImageCopyBlock item={fractal} showAllOnSmall />
        <CopyImageBlock item={luster} showAllOnSmall />
        <ImageCopyBlock item={nightmare} showAllOnSmall />
        <div {...textContainer}>
          <h1>The Story</h1>
          <Animated>
            I developed the concepts to be in sync with the rhythms of the day.
            In the morning, colors are chosen from photos of sunrises to create
            warmth, in the evening cooler blues are used as the sun goes down.
          </Animated>
          <Animated>
            By following nature's lead, the installation becomes part of the
            community.
          </Animated>
        </div>
        <div {...wrapper}>
          {map(set1, (image, index) => {
            if (index % 4 === 0) {
              return row1(image, index);
            } else {
              return row3(image, index, '16x9');
            }
          })}
        </div>
        <div {...wrapper}>
          {map(warpImages, (image, index) => {
            return row3(image, index, '1x1');
          })}
        </div>
        <div {...wrapper}>
          {map(cloudsImages, (image, index) => {
            return row2(image, index);
          })}
        </div>
        <div {...textContainer}>
          <Animated>
            I have many people to thank for giving me a giant canvas to make art
            on.
          </Animated>
          <Animated>
            To{' '}
            <a
              href="http://eichingersculpture.com/"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              Marty Eichinger
            </a>, the client who trusted my artist intuition and was never
            prescriptive, Matt Scheick: project fabricator, Adrien 'Finch'
            Thebo: engineer of the translation layer that puts my pixels on the
            LEDs, and Talik Eichinger: the glue that held everything together,
            thank you. Without the talent and dedication of the entire team at
            MEZR, this project would not exist.
          </Animated>
          <Animated>
            I'd also like to thank the community at{' '}
            <a
              href="https://www.shadertoy.com/"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              shadertoy
            </a>, who has been a continuous source of learning and inspiration
            for me.
          </Animated>
          <Animated as="h2">I want to see it</Animated>
          <Animated>
            We're wrapping up the final details on the hardware and it'll be
            live soon. Follow me on{' '}
            <a
              target="_blank"
              rel="nofollow noopener noreferrer"
              href="https://twitter.com/lucastswick"
            >
              Twitter
            </a>{' '}
            for the launch date!
          </Animated>
        </div>
        <div {...wideContainer} {...removePaddingTop}>
          <Video videoId={'262680431'} sources={conceptPoster} />
        </div>
        <WorkNav prev={prev} next={next} />
      </div>
    );
  }
}

export default Lightblades;



// WEBPACK FOOTER //
// ./src/pages/Lightblades.js